/**
 * DailyVitalsTile (iter1067) — „Днес": тегло, кръвно, сън, настроение.
 * Едно въвеждане храни няколко близнака.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { CalendarCheck } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "weight_kg", label: "Тегло, кг", step: "0.1", placeholder: "72.4" },
  { key: "systolic", label: "Горно налягане", step: "1", placeholder: "118" },
  { key: "diastolic", label: "Долно налягане", step: "1", placeholder: "76" },
  { key: "sleep_hours", label: "Сън, часове", step: "0.5", placeholder: "7.5" },
  { key: "mood", label: "Настроение (1-10)", step: "1", placeholder: "7" },
];

export default function DailyVitalsTile() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/daily-vitals`).then(({ data }) => {
      setData(data);
      setForm(data.today || {});
    }).catch(() => {});
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const body = {};
    FIELDS.forEach(({ key }) => {
      const v = form[key];
      if (v !== "" && v !== null && v !== undefined) body[key] = Number(v);
    });
    if (Object.keys(body).length === 0) { toast.error("Въведете поне една стойност."); return; }
    setSaving(true);
    try {
      const { data: res } = await axios.post(`${API}/daily-vitals`, body);
      toast.success(`Записано. Отива към: ${(res.twins_bg || []).join(", ")}`);
      load();
    } catch {
      toast.error("Записът не мина.");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;
  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";

  return (
    <div className={`rounded-2xl border p-4 ${cardCls}`} data-testid="daily-vitals-tile">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-bold inline-flex items-center gap-2">
          <CalendarCheck className="w-4 h-4 text-sky-500" /> {data.title_bg}
        </h3>
        {data.today && (
          <span className="text-[11px] opacity-60" data-testid="daily-vitals-saved">
            днешният запис е попълнен
          </span>
        )}
      </div>
      <p className="text-[11px] opacity-60 mt-1 max-w-2xl">{data.hint_bg}</p>

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2">
        {FIELDS.map(({ key, label, step, placeholder }) => (
          <label key={key} className="text-[10px] opacity-70">
            {label}
            <input
              type="number"
              step={step}
              value={form[key] ?? ""}
              placeholder={placeholder}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
              data-testid={`daily-vitals-${key}`}
              className={`mt-0.5 w-full rounded-lg px-2 py-1.5 text-sm outline-none border ${
                isDark ? "bg-slate-950/60 border-slate-700" : "bg-white border-slate-200"}`}
            />
          </label>
        ))}
      </div>

      <Button size="sm" className="mt-3" onClick={save} disabled={saving}
        data-testid="daily-vitals-save-btn">
        {saving ? "Записвам…" : "Запишете за днес"}
      </Button>
    </div>
  );
}
